import React from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { X } from 'lucide-react';

interface TermsOfServiceModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export function TermsOfServiceModal({ isOpen, onClose }: TermsOfServiceModalProps) {
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl w-full h-[90vh] p-0 gap-0 flex flex-col">
        <DialogHeader className="px-6 py-4 border-b bg-card flex-shrink-0">
          <DialogTitle className="text-lg font-semibold">Termini di Servizio</DialogTitle>
        </DialogHeader>
        
        <div className="relative flex-1 bg-background min-h-0">
          <div className="p-6 overflow-y-auto h-full">
            <div className="prose prose-gray dark:prose-invert max-w-none">
              <div className="text-center mb-6">
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
                  Termini di Servizio – Gildy.it
                </h1>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Ultimo aggiornamento: 12 giugno 2025
                </p>
              </div>

              <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-6">
                Benvenuto su Gildy.it. Utilizzando il sito web e i servizi offerti da Gildy, l'utente accetta i seguenti Termini di Servizio. Si prega di leggere attentamente quanto segue.
              </p>

              <div className="space-y-6">
                <section>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                    1. Descrizione del servizio
                  </h3>
                  <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-3">
                    Gildy è una piattaforma digitale che fornisce:
                  </p>
                  <ul className="list-disc pl-6 space-y-2 text-gray-700 dark:text-gray-300">
                    <li>Informazioni e strumenti per comprendere il valore dei metalli preziosi (oro e argento);</li>
                    <li>Quotazioni in tempo reale e contenuti generati tramite intelligenza artificiale a scopo informativo;</li>
                    <li>Visibilità e vetrina online per operatori del settore compro oro e affini.</li>
                  </ul>
                  <p className="text-gray-700 dark:text-gray-300 leading-relaxed mt-3">
                    Gildy non acquista, vende o commercia metalli preziosi e non partecipa in alcun modo alle trattative tra utenti e operatori. Gildy non è responsabile per le condizioni di acquisto, valutazione o vendita praticate da terzi.
                  </p>
                </section>

                <section>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                    2. Utilizzo del sito
                  </h3>
                  <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-3">
                    L'utente si impegna a:
                  </p>
                  <ul className="list-disc pl-6 space-y-2 text-gray-700 dark:text-gray-300">
                    <li>Non utilizzare il sito per scopi illegali o contrari alle leggi vigenti;</li>
                    <li>Non utilizzare le informazioni presenti sul sito come consulenza finanziaria, legale o commerciale;</li>
                    <li>Non tentare di compromettere la sicurezza, integrità o funzionalità del sito.</li>
                  </ul>
                </section>

                <section>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                    3. Contenuti e responsabilità
                  </h3>
                  <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-3">
                    Tutte le quotazioni, stime, valutazioni, previsioni e contenuti presenti su Gildy.it sono forniti a solo scopo informativo e non costituiscono consigli finanziari, inviti all'investimento o sollecitazioni alla vendita di beni personali.
                  </p>
                  <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                    Gildy non garantisce l'accuratezza, completezza o aggiornamento costante delle informazioni e non si assume alcuna responsabilità per decisioni prese sulla base dei contenuti del sito.
                  </p>
                </section>

                <section>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                    4. Servizi per operatori (compro oro)
                  </h3>
                  <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-3">
                    Gli operatori del settore iscritti a Gildy possono utilizzare la piattaforma come vetrina digitale per presentare i propri servizi agli utenti. L'inserimento dei dati aziendali è a cura del titolare dell'attività, che ne garantisce la veridicità.
                  </p>
                  <p className="text-gray-700 dark:text-gray-300 leading-relaxed mb-3">
                    Gildy:
                  </p>
                  <ul className="list-disc pl-6 space-y-2 text-gray-700 dark:text-gray-300">
                    <li>Non certifica né garantisce la qualità o legalità dei servizi offerti dai commercianti;</li>
                    <li>Non interviene nelle trattative commerciali tra operatori e utenti;</li>
                    <li>Si riserva il diritto di rimuovere profili falsi o contenuti non conformi.</li>
                  </ul>
                </section>

                <section>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                    5. Proprietà intellettuale
                  </h3>
                  <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                    Tutti i contenuti presenti su Gildy.it, inclusi testi, grafici, loghi, immagini e software, sono di proprietà di Gildy o dei rispettivi licenziatari. È vietata la riproduzione o diffusione non autorizzata.
                  </p>
                </section>

                <section>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                    6. Modifiche ai termini
                  </h3>
                  <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                    Gildy si riserva il diritto di aggiornare o modificare i presenti Termini di Servizio in qualsiasi momento, notificando gli utenti attraverso il sito stesso. L'uso continuativo del sito implica l'accettazione delle modifiche.
                  </p>
                </section>

                <section>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                    7. Legge applicabile
                  </h3>
                  <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                    I presenti Termini sono regolati dalla legge italiana. Per ogni controversia sarà competente il Foro di Roma, salvo diversi accordi tra le parti.
                  </p>
                </section>

                <section>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                    8. Contatti
                  </h3>
                  <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
                    Per qualsiasi richiesta o segnalazione, è possibile contattare l'assistenza Gildy all'indirizzo: 
                    <a href="mailto:info@gildy.it" className="text-gold hover:text-gold/80 ml-1">
                      info@gildy.it
                    </a>
                  </p>
                </section>
              </div>
            </div>
          </div>
        </div>
        
        <div className="px-6 py-3 border-t bg-muted/30 flex-shrink-0">
          <div className="flex items-center justify-end">
            <Button
              variant="outline"
              size="sm"
              onClick={onClose}
              className="gap-2"
            >
              <X className="h-4 w-4" />
              Chiudi
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
